import { Typography } from '@material-ui/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROWS_PER_PAGE } from '../App';
import { AppDialog } from '../components/AppDialog';
import { AppToolbar } from '../components/AppToolbar';
import { TextFieldComponent, NumberField } from '../external/ensolvers-core-frontend-mui/components/Common/FormComponents';
import TableBasedCrud from '../external/ensolvers-core-frontend-mui/components/TableBaseCrud';
import NotificationService from '../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService';
import RoleUtils from '../utils/RoleUtils';

import Property from '../models/Property';
import PropertiesService from '../services/PropertiesService';
import PropertyEditDialog from '../components/Properties/PropertyEditDialog';
import ActionButton, { ActionsMenuEntryIcons } from '../components/Worklog/ActionButton';

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { ColorUtils } from '../utils/ColorUtils';
import { useStyles as formStyles } from "../components/Main/formStyles";
import { Box } from '@mui/material';

export default function PropertiesList() {
  const { t } = useTranslation();

  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(undefined);
  const [editProperty, setEditProperty] = useState<Property | undefined>(undefined);
  const customStyles = formStyles();
  
  const handleOnDeleteProperty = async () => {
    if (!selectedProperty) return;

    try {
      await PropertiesService.deleteProperty(selectedProperty);
      setSelectedProperty(undefined);
      NotificationService.info('Property deleted successfully');
      setUpdate(Date.now());
    } catch (error) {
      NotificationService.error('Error trying to delete a property');
    }
  };

  function getAdditionalActions(instance: Property): ActionsMenuEntryIcons[] {
    if (!RoleUtils.isAdmin()) return (
      [{
        title: t("N/A"),
        action: () => {},
      }]
    )
    const options = [
      {
        title: t("Edit"),
        action: () => setEditProperty(instance),
        icon:<EditIcon/>
      },
      {
        title: t("Delete"),
        action: () => setSelectedProperty(instance),
        icon:<DeleteOutlineIcon/>
      },      
    ];

    return options;
  }

  return (
    <Box sx={{ "&>div:nth-child(2)": { boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" } }}>
      <AppToolbar title={t('Properties')} />

      <TableBasedCrud<Property>
        headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
        buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
        cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
        dialogStyles={ customStyles.root }        
        buttonContainer={ customButtonsContainer }
        gridButtonContainer={ gridButtonContainer }
        addButtonBuilder={ customButtonBuilder }
        elementName={'Properties'}
        rowsPerPage={ROWS_PER_PAGE}
        forceUpdateOn={update}
        denseTable
        stickyHeader
        stickyPagination
        onDataFetch={async (pageNumber, count) => (await PropertiesService.getAll(pageNumber, count)).getContentOrThrowError()}
        onCreate={(property) =>
          PropertiesService.create(property).then((data) =>
            data.getContentOrNotifyError(() => NotificationService.info(`Property created successfully`))
          )
        }
        onClickEdit={(property) => setEditProperty(property)}
        resetValue={{ key: "", content: "", sinceVersion: null }}
        buildAdditionalActions={(instance) => [
          {
            title: t('Edit'),
            action: () => setEditProperty(instance),
          },
          {
            title: t('Delete'),
            action: () => setSelectedProperty(instance),
          },
        ]}
        renderDialogContent={(register, errors, control) => (
          <>
            <TextFieldComponent register={register} errors={errors} control={control} propertyName={'key'} label={'Key'} required={true} />
            <TextFieldComponent
              register={register}
              errors={errors}
              control={control}
              propertyName={'content'}
              label={'Content'}
              required={true}
            />
            <NumberField
              register={register}
              errors={errors}
              control={control}
              propertyName={'sinceVersion'}
              label={'Since version'}
              required={true}
            />
          </>
        )}
        tableColumns={[
          {
            header: t('Key'),
            content: (instance) => <>{instance.key}</>,
          },
          {
            header: t("Content"),
            content: (instance) => (
              <>{instance.content.length > 50 ? instance.content.substring(0, 100).concat("...") : instance.content}</>
            ),
          },
          {
            header: t('Since version'),
            content: (instance) => <>{instance.sinceVersion}</>,
          },
          {
            header: t("Actions"),
            content: (instance) => {
              const actions = getAdditionalActions(instance)
              return (<>
                {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                </>
              )
            },
          },           
        ]}
        hideActions={true}
      />

      {/* Dialog to delete properties */}
      <AppDialog
        open={!!selectedProperty}
        onClose={() => setSelectedProperty(undefined)}
        title={'Delete'}
        content={
          <Typography>
            {`The next holiday is going to be eliminated: ${selectedProperty?.key}. 
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[{ label: 'Confirm delete', onClick: () => handleOnDeleteProperty() }]}
      />

      <PropertyEditDialog
        editProperty={editProperty}
        onClose={() => setEditProperty(undefined)}
        onSave={(property) =>
          PropertiesService.updateProperty(property).then((data) =>
            data.getContentOrNotifyError(() => {
              NotificationService.info(`Property updated successfully`);
              setUpdate(Date.now());
            })
          )
        }
      />
    </Box>
  );
}
